import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { api, getToken } from "@/lib/api";
import Loader from "@/components/Loader";
import Logo from "@/components/Logo";

/**
 * /a/:shortId — public deep-link resolver.
 *   - Guest        → redirect to /login?next=/a/:shortId
 *   - Auth + ok    → navigate to /alert/:id
 *   - Auth + 404   → render "Alerte introuvable" with a back button
 *   - Auth + other → render generic error with retry
 */
export default function PublicAlertRedirect() {
  const { shortId } = useParams();
  const navigate = useNavigate();

  // status: 'resolving' | 'not_found' | 'error'
  const [status, setStatus] = useState("resolving");
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (!getToken()) {
        navigate(`/login?next=/a/${encodeURIComponent(shortId)}`, { replace: true });
        return;
      }
      try {
        const { data } = await api.get(`/alerts/by-short/${encodeURIComponent(shortId)}`);
        navigate(`/alert/${data.id}`, { replace: true });
      } catch (err) {
        const code = err?.response?.status;
        if (code === 404) {
          setStatus("not_found");
        } else if (code === 401) {
          // Token expired between guard and request → bounce to login with next
          navigate(`/login?next=/a/${encodeURIComponent(shortId)}`, { replace: true });
        } else {
          setErrorMessage(
            err?.response?.data?.detail || err?.message || "Erreur de chargement."
          );
          setStatus("error");
        }
      }
    };
    run();
  }, [shortId, navigate]);

  if (status === "resolving") return <Loader />;

  // Not-found / error state
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[var(--sa-subtle)] px-5 py-10"
      data-testid="public-alert-not-found-screen"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="sa-card w-full max-w-[420px] px-7 py-9 text-center"
      >
        <div className="flex flex-col items-center">
          <Logo size={48} />
          <div
            className="mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600"
            aria-hidden
          >
            <AlertTriangle size={22} />
          </div>
          <h1
            className="mt-4 text-[22px] font-semibold text-slate-900"
            style={{ letterSpacing: "-0.02em" }}
            data-testid="public-alert-not-found-title"
          >
            {status === "not_found" ? "Alerte introuvable" : "Erreur de chargement"}
          </h1>
          <p className="mt-2 text-[14px] text-slate-500 leading-snug">
            {status === "not_found" ? (
              <>
                Le lien partagé pointe vers une alerte qui n'existe pas ou a été
                supprimée.
                <br />
                Identifiant&nbsp;: <span className="font-mono text-slate-600">#{shortId}</span>
              </>
            ) : (
              errorMessage
            )}
          </p>

          <Link
            to="/home"
            replace
            data-testid="public-alert-back-home"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-[16px] bg-[var(--sa-blue)] px-5 py-3 text-[14px] font-semibold text-white transition hover:bg-[var(--sa-blue-dark)]"
          >
            <ArrowLeft size={15} />
            Retour à l'accueil
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
